import { useState } from 'react';
import { Loader2, UserPlus } from 'lucide-react';
import { useCreateCreator } from '../hooks/useCreateCreator';

const inputClass = "w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors";
const labelClass = "text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block";

interface AcademyCreateCreatorFormProps {
    onCreated: (creatorId?: string) => void;
    onCancel: () => void;
}

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function AcademyCreateCreatorForm({ onCreated, onCancel }: AcademyCreateCreatorFormProps) {
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [channelName, setChannelName] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [linkUserId, setLinkUserId] = useState('');
    const createMutation = useCreateCreator();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const url = youtubeUrl.trim();
        const name = channelName.trim();
        const trimmedLink = linkUserId.trim();
        if (!url || !name) return;
        if (trimmedLink && !uuidRegex.test(trimmedLink)) {
            return;
        }

        try {
            const creatorId = await createMutation.mutateAsync({
                p_youtube_channel_url: url,
                p_channel_display_name: name,
                p_description: description.trim() || null,
                p_image_url: imageUrl.trim() || null,
                p_status: 'approved',
                ...(trimmedLink ? { p_link_user_id: trimmedLink } : {}),
            });
            onCreated(creatorId);
        } catch {
            // Error handled by mutation
        }
    };

    return (
        <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <UserPlus size={16} />
                Nuevo Creador (Canal externo)
            </h3>
            <p className="text-xs text-muted-foreground">
                Añade un canal de YouTube. Si el creador ya tiene cuenta en AiPeces, indica su UUID de usuario abajo para vincular el canal (si no, queda como canal externo hasta que reclame). Los vídeos importados no se publican en el feed de comunidad; el feed solo refleja lo que el creador publique desde la app.
            </p>

            <div>
                <label className={labelClass}>URL del canal de YouTube *</label>
                <input
                    type="url"
                    value={youtubeUrl}
                    onChange={e => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/@canal"
                    className={inputClass}
                    required
                />
            </div>

            <div>
                <label className={labelClass}>Nombre para mostrar *</label>
                <input
                    type="text"
                    value={channelName}
                    onChange={e => setChannelName(e.target.value)}
                    placeholder="Nombre del canal o creador"
                    className={inputClass}
                    required
                />
            </div>

            <div>
                <label className={labelClass}>Descripción</label>
                <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Breve descripción del canal"
                    className={inputClass}
                    rows={2}
                />
            </div>

            <div>
                <label className={labelClass}>URL de imagen (avatar)</label>
                <input
                    type="url"
                    value={imageUrl}
                    onChange={e => setImageUrl(e.target.value)}
                    placeholder="https://..."
                    className={inputClass}
                />
            </div>

            <div>
                <label className={labelClass}>Vincular usuario AiPeces (opcional)</label>
                <input
                    type="text"
                    value={linkUserId}
                    onChange={e => setLinkUserId(e.target.value)}
                    placeholder="UUID del usuario (profiles.user_id)"
                    className={inputClass}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                    Si el creador ya está registrado, pega su UUID para asociar el canal a su cuenta. Déjalo vacío solo si el canal es totalmente externo.
                </p>
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    disabled={
                        createMutation.isPending ||
                        !youtubeUrl.trim() ||
                        !channelName.trim() ||
                        (Boolean(linkUserId.trim()) && !uuidRegex.test(linkUserId.trim()))
                    }
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                    Crear Creador
                </button>
            </div>
        </form>
    );
}
